import { useCallback, useMemo, useState } from 'react';
import { useGetProgramScheduledWorkoutsQuery } from 'generated/graphql';
import {
  ExistingScheduleTime,
  findScheduleTime,
  toExistingScheduleTimes,
} from './scheduleGroups.domain';
import { useIsGroupedWorkoutSchedulingEnabled } from './useIsGroupedWorkoutSchedulingEnabled';

type UseJoinScheduleGroupParams = {
  influencerId: number;
  programId: number | undefined;
  workoutId: number | undefined;
  publishedAt: string | undefined;
  setPublishedAt: (publishedAt: string) => void;
};

export const useJoinedTime = (
  times: Array<ExistingScheduleTime>,
  publishedAt: string | undefined,
  setPublishedAt: (publishedAt: string) => void,
) => {
  const [isCleared, setIsCleared] = useState(false);
  const joined = isCleared ? undefined : findScheduleTime(times, publishedAt);

  const join = useCallback(
    (time: ExistingScheduleTime) => {
      setIsCleared(false);
      setPublishedAt(time.publishedAt);
    },
    [setPublishedAt],
  );
  const clear = useCallback(() => setIsCleared(true), []);
  const reset = useCallback(() => setIsCleared(false), []);

  return { joined, join, clear, reset };
};

export const useJoinScheduleGroup = ({
  influencerId,
  programId,
  workoutId,
  publishedAt,
  setPublishedAt,
}: UseJoinScheduleGroupParams) => {
  const isEnabled = useIsGroupedWorkoutSchedulingEnabled(influencerId);
  const { data } = useGetProgramScheduledWorkoutsQuery({
    variables: { programId: programId ?? 0 },
    skip: !isEnabled || !programId,
    fetchPolicy: 'cache-and-network',
  });
  const scheduled = isEnabled ? data?.program?.scheduledWorkouts : undefined;
  const times = useMemo(
    () =>
      toExistingScheduleTimes(
        (scheduled ?? []).flatMap((w) =>
          w && typeof w.publishedAt === 'string'
            ? [{ id: w.id, publishedAt: w.publishedAt }]
            : [],
        ),
        workoutId ? [workoutId] : [],
        new Date(),
      ),
    [scheduled, workoutId],
  );

  return {
    times,
    ...useJoinedTime(times, publishedAt, setPublishedAt),
    programName: (isEnabled && data?.program?.name) || undefined,
  };
};
